import { BookOpen, FileText } from 'lucide-react';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';

import { Card, CardContent, CardHeader } from '@/components/ui';
import { cn } from '@/lib/cn';

// Vite glob import: bundles every locale's markdown at build time as a raw
// string keyed by path. New topics only need a markdown file in
// `topics/<lang>/<id>.md` — they show up automatically.
const TOPICS = import.meta.glob('./topics/*/*.md', {
  query: '?raw',
  import: 'default',
  eager: true,
}) as Record<string, string>;

interface Topic {
  id: string;
  defaultTitle: string;
  icon: typeof FileText;
}

// Order + display labels. The id matches the markdown filename without `.md`.
const TOPIC_INDEX: Topic[] = [
  { id: 'pipeline', defaultTitle: 'How the pipeline works', icon: BookOpen },
  { id: 'error-types', defaultTitle: 'Error queue types', icon: FileText },
  { id: 'glossary', defaultTitle: 'Glossary', icon: BookOpen },
];

/**
 * Static help center — markdown pages bundled at build time and rendered with
 * `react-markdown`. Translatable per topic via `topics/<lang>/<id>.md`;
 * missing translations fall back to the English version.
 */
export function HelpCenter() {
  const { t, i18n } = useTranslation('studio');
  const [active, setActive] = useState<string>(TOPIC_INDEX[0]!.id);
  const lang = i18n.resolvedLanguage ?? 'en';

  const body = useMemo(() => {
    const tryLang = TOPICS[`./topics/${lang}/${active}.md`];
    if (tryLang) return tryLang;
    return TOPICS[`./topics/en/${active}.md`] ?? '';
  }, [lang, active]);

  return (
    <div className="grid h-[calc(100vh-3.5rem)] grid-cols-[260px_1fr] gap-3 p-3">
      <Card className="overflow-auto">
        <CardHeader>
          <h2 className="text-h2 text-brand-900">{t('help.title')}</h2>
          <p className="mt-1 text-small text-slate-600">{t('help.subtitle')}</p>
        </CardHeader>
        <CardContent className="p-0">
          <ul className="divide-y divide-slate-100">
            {TOPIC_INDEX.map((topic) => {
              const isActive = topic.id === active;
              const title = t(`help.topics.${topic.id}`, { defaultValue: topic.defaultTitle });
              return (
                <li key={topic.id}>
                  <button
                    type="button"
                    onClick={() => setActive(topic.id)}
                    className={cn(
                      'flex w-full items-center gap-2 px-4 py-2 text-left text-small hover:bg-slate-50',
                      isActive && 'bg-brand-50 font-medium text-brand-900',
                    )}
                  >
                    <topic.icon className="h-3.5 w-3.5 text-slate-500" aria-hidden />
                    {title}
                  </button>
                </li>
              );
            })}
          </ul>
        </CardContent>
      </Card>

      <Card className="overflow-auto">
        <CardContent className="prose prose-slate max-w-none py-6">
          <ReactMarkdown>{body}</ReactMarkdown>
        </CardContent>
      </Card>
    </div>
  );
}
